import { Trans, useLingui } from "@lingui/react/macro";
import {
  addJitActivityInVisit,
  type INewJitActivityInVisit,
} from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import TextArea from "@/components/TextArea/TextArea";
import CheckmarkIcon from "@medoma/components/icons/CheckmarkIcon";
import { deducedError } from "@/Utils/ErrorUtils";
import { generateRandomUUID } from "@/Utils/UniqueId";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

interface IPostRegisterActivityFormData {
  description: string;
  title: string;
}

const PostRegisterActivity = ({
  onSuccess,
  routeId,
  visitId,
}: {
  onSuccess: () => void;
  routeId: string;
  visitId: string;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<IPostRegisterActivityFormData>();

  const { mutate: addJitActivityMutation, isPending: isAdding } = useMutation({
    mutationFn: ({ newActivity }: { newActivity: INewJitActivityInVisit }) =>
      addJitActivityInVisit(newActivity),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: () => {
      reset();
      return onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        const newActivity: INewJitActivityInVisit = {
          activityId: generateRandomUUID(),
          title: formData.title,
          description: formData.description,
          visitId,
          routeId,
        };

        addJitActivityMutation({ newActivity });
      })}
    >
      {isAdding ? (
        <Loading message={t`Efterregistrerar aktivitet`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <InputField
            label={t`Titel`}
            showOptionalLabel={false}
            errorMessage={errors.title?.message}
            {...register(`title`, {
              required: {
                value: true,
                message: t`Aktiviteten behöver en titel`,
              },
            })}
          />
          <TextArea
            label={t`Beskrivning`}
            showOptionalLabel
            errorMessage={errors.description?.message}
            {...register(`description`)}
          />
          <FilledButton type="submit" width="fill-container">
            <CheckmarkIcon />
            <Trans>Lägg till aktiviteten</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};

export { PostRegisterActivity };
